import { isEvolutionAppointment } from "@/components/schedule/evolution/isEvolutionAppointment";
import {
  TELECONSULTORIA_PARTICIPANT_TYPE,
  TELECONSULTORIA_SERVICE_CATEGORY,
} from "../constants";
import type { TeleconsultoriaActor, TeleconsultoriaGuest } from "../types";
import { readWherebyRoom } from "./virtualService";

export function isTeleconsultoriaSchedule(
  schedule: fhir5.Schedule | undefined,
): boolean {
  if (!schedule?.serviceCategory?.length) return false;
  return schedule.serviceCategory.some((category) =>
    category.coding?.some(
      (coding) =>
        coding.code === TELECONSULTORIA_SERVICE_CATEGORY.code &&
        coding.system === TELECONSULTORIA_SERVICE_CATEGORY.system,
    ),
  );
}

/** HCS criado pela teleconsultoria (category própria). O HCS clínico reutilizado não tem. */
export function isTeleconsultoriaHealthcareService(
  hcs: fhir5.HealthcareService | undefined,
): boolean {
  if (!hcs?.category?.length) return false;
  return hcs.category.some((category) =>
    category.coding?.some(
      (coding) =>
        coding.code === TELECONSULTORIA_SERVICE_CATEGORY.code &&
        coding.system === TELECONSULTORIA_SERVICE_CATEGORY.system,
    ),
  );
}

export function actorByType(
  actors: fhir5.Reference[] | undefined,
  type: string,
): TeleconsultoriaActor | undefined {
  const actor = actors?.find((item) => item.reference?.includes(`${type}/`));
  if (!actor?.reference) return undefined;
  return { reference: actor.reference, display: actor.display || "" };
}

export function guestFromSchedule(
  schedule: fhir5.Schedule,
): TeleconsultoriaGuest | undefined {
  const practitioner = actorByType(schedule.actor, "Practitioner");
  if (!practitioner) return undefined;
  const healthcareService = actorByType(schedule.actor, "HealthcareService");
  return {
    practitioner,
    ...(healthcareService ? { healthcareService } : {}),
  };
}

function practitionerParticipants(appointment: fhir5.Appointment | undefined) {
  return (
    appointment?.participant?.filter((item) =>
      item.actor?.reference?.includes("Practitioner/"),
    ) || []
  );
}

/**
 * Convidado = Practitioner extra (não o primeiro) em evolução com sala.
 * O primeiro Practitioner permanece o host.
 * Outros tipos de atendimento: só conta o Practitioner marcado com CON.
 */
export function teleconsultoriaGuestRoomUrl(
  appointment: fhir5.Appointment | undefined,
  practitionerRef: string,
): string | undefined {
  if (!appointment || !practitionerRef) return undefined;

  const room = readWherebyRoom(appointment.virtualService);
  if (!room?.addressUrl) return undefined;

  // Consulta comum pode ter outro Practitioner que não é teleconsultor:
  // sem a marcação CON, não é convite.
  if (!isEvolutionAppointment(appointment)) {
    const isMarkedGuest = practitionerParticipants(appointment).some(
      (item) =>
        isTeleconsultoriaParticipant(item) &&
        item.actor?.reference === practitionerRef,
    );
    return isMarkedGuest ? room.addressUrl : undefined;
  }

  const practitioners = practitionerParticipants(appointment);
  if (practitioners.length < 2) return undefined;
  if (practitioners[0]?.actor?.reference === practitionerRef) return undefined;

  const isGuest = practitioners
    .slice(1)
    .some((item) => item.actor?.reference === practitionerRef);
  return isGuest ? room.addressUrl : undefined;
}

export function existingActorRefs(
  participants: Array<{ actor?: { reference?: string } }> | undefined,
): Set<string> {
  return new Set(
    (participants || [])
      .map((item) => item.actor?.reference)
      .filter((reference): reference is string => Boolean(reference)),
  );
}

type ParticipantLike = {
  type?: Array<{ coding?: Array<{ system?: string; code?: string }> }>;
  actor?: { reference?: string; display?: string };
};

/** Participant gravado como teleconsultor (type CON). */
export function isTeleconsultoriaParticipant(
  participant: ParticipantLike | undefined,
): boolean {
  return Boolean(
    participant?.type?.some((type) =>
      type.coding?.some(
        (coding) =>
          coding.system === TELECONSULTORIA_PARTICIPANT_TYPE.system &&
          coding.code === TELECONSULTORIA_PARTICIPANT_TYPE.code,
      ),
    ),
  );
}

function toActor(participant?: ParticipantLike): TeleconsultoriaActor | undefined {
  const reference = participant?.actor?.reference;
  if (!reference) return undefined;
  return { reference, display: participant?.actor?.display || "" };
}

/**
 * Convidado de um Appointment/Encounter.
 * Registros antigos sem marcação: Practitioner depois do primeiro. HCS antigo não é inferido.
 */
export function teleconsultoriaGuestFromParticipants(
  participants: ParticipantLike[] | undefined,
): Partial<TeleconsultoriaGuest> {
  const list = (participants || []).filter(Boolean);
  const marked = list.filter(isTeleconsultoriaParticipant);
  const byType = (items: ParticipantLike[], type: string) =>
    items.find((item) => item.actor?.reference?.includes(`${type}/`));

  if (marked.length) {
    const practitioner = toActor(byType(marked, "Practitioner"));
    const healthcareService = toActor(byType(marked, "HealthcareService"));
    return {
      ...(practitioner ? { practitioner } : {}),
      ...(healthcareService ? { healthcareService } : {}),
    };
  }

  const practitioners = list.filter((item) =>
    item.actor?.reference?.includes("Practitioner/"),
  );
  const legacy = toActor(practitioners[1]);
  return legacy ? { practitioner: legacy } : {};
}

/**
 * Convidado conforme o tipo do Appointment.
 * Evolução: regra completa (inclui registros antigos sem marcação).
 * Outros tipos: só participants marcados com CON.
 */
export function teleconsultoriaGuestOfAppointment(
  appointment: fhir5.Appointment | undefined,
): Partial<TeleconsultoriaGuest> {
  if (!appointment) return {};
  if (isEvolutionAppointment(appointment)) {
    return teleconsultoriaGuestFromParticipants(appointment.participant);
  }
  // Só os marcados: sem nenhum, a regra antiga recebe lista vazia e devolve {}.
  const marked = (appointment.participant || []).filter(
    isTeleconsultoriaParticipant,
  );
  return teleconsultoriaGuestFromParticipants(marked);
}

/** Actors do convidado que ainda não estão no recurso. */
export function guestActorsToAdd(
  guest: TeleconsultoriaGuest,
  already: Set<string>,
): TeleconsultoriaActor[] {
  const actors = [guest.practitioner, guest.healthcareService].filter(
    (actor): actor is TeleconsultoriaActor => Boolean(actor?.reference),
  );
  return actors.filter((actor) => !already.has(actor.reference));
}
